import { useFieldInfo, useTsController } from "@ts-react/form";
import React, { useId } from "react";
import type { GetProps, TamaguiElement, Input as TInput } from "tamagui";
import type { IconProps } from "@tamagui/helpers-icon";
import { BentoInput } from "../bento/inputParts";
import { Fieldset } from "./Fieldset";
import { FormError } from "./FormError";
import { useFormFieldOptions } from "./FormFieldOptionsProvider";
export type InputFieldProps = Pick<GetProps<typeof TInput>, "size" | "autoFocus" | "disabled" | "onSubmitEditing" | "inputMode" | "keyboardType" | "maxLength" | "placeholder" | "returnKeyType"> & {
  rightIcon?: React.ComponentType<IconProps>;
};
export const InputField = (props: InputFieldProps & {
  formatInput?: (text: string) => string;
}) => {
  const {
    field,
    error,
    formState: {
      isSubmitting
    }
  } = useTsController<string>();
  const {
    formatInput,
    size,
    disabled: disabledProp,
    rightIcon: RightIcon,
    ...inputProps
  } = props;
  const {
    label,
    placeholder,
    isOptional
  } = useFieldInfo();
  const {
    hideOptionalLabel
  } = useFormFieldOptions();
  const inputRef = React.useRef<TamaguiElement | null>(null);
  const id = useId();
  const inputId = `${id}_input`;
  const disabled = isSubmitting || disabledProp;
  const setInputFocus = () => {
    inputRef.current?.focus();
  };
  return <Fieldset data-sentry-element="Fieldset" data-sentry-component="InputField" data-sentry-source-file="InputField.tsx">
      <BentoInput size={size || "$4"} key={inputId} data-sentry-element="BentoInput" data-sentry-source-file="InputField.tsx">
        {label ? <BentoInput.Label color="$textSubtle" htmlFor={inputId} flexGrow={0} alignSelf="flex-start">
            {label}
            {isOptional && !hideOptionalLabel ? ` (Optional)` : null}
          </BentoInput.Label> : null}
        <BentoInput.Box onPress={() => setInputFocus()} disabled={disabled} tone={error ? "negative" : "default"} data-sentry-element="BentoInput.Box" data-sentry-source-file="InputField.tsx">
          <BentoInput.Area id={inputId} testID={field.name} hasLabel={!!label} placeholder={placeholder} overflow="hidden" flex={1} paddingRight={RightIcon ? 0 : undefined} backgroundColor={disabled ? "$bgDisabledSubtle" : "$surface2"}
        // Empty string instead of undefined so that the component
        // does not transition from "unmanaged" to "managed" (which
        // displays an error log in the web console).
        value={field.value ?? ""} onChangeText={text => {
          field.onChange(formatInput?.(text) ?? text);
        }} onBlur={field.onBlur} disabled={disabled} ref={ref => {
          field.ref(ref);
          inputRef.current = ref;
        }} {...inputProps} data-sentry-element="BentoInput.Area" data-sentry-source-file="InputField.tsx" />
          {RightIcon ? <BentoInput.Icon paddingHorizontal="$3">
              <RightIcon color="$iconDefault" strokeWidth={2} scale={1.25} />
            </BentoInput.Icon> : null}
        </BentoInput.Box>
        <FormError errorMessage={error?.errorMessage} data-sentry-element="FormError" data-sentry-source-file="InputField.tsx" />
      </BentoInput>
    </Fieldset>;
};